import React, { useEffect, useRef, useState } from "react";
import { STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useGetCmpEmailCaptureAddressStatusQuery } from "../../../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";

const captureErrorContext = getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: [] });

const EmailCaptureAddressSection = ({ chamberId }: { chamberId: string }) => {
  const { data, loading, error, refetch } = useGetCmpEmailCaptureAddressStatusQuery({ variables: { chamberId }, skip: !chamberId, context: captureErrorContext });
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const status = data?.getCmpEmailCaptureAddressStatus;
  const address = status?.address;
  const copySequence = useRef(0);
  useEffect(() => {
    copySequence.current += 1; setCopied(false); setCopyError(false);
    return () => { copySequence.current += 1; };
  }, [chamberId, address]);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const copy = async () => {
    if (!address) return;
    const request = ++copySequence.current;
    try { await navigator.clipboard.writeText(address); if (request === copySequence.current) { setCopied(true); setCopyError(false); } }
    catch { if (request === copySequence.current) { setCopied(false); setCopyError(true); } }
  };
  const retry = async () => {
    try { await refetch(); }
    catch { /* The query displays the error, and Apollo reports unexpected failures. */ }
  };
  return <section className="flex flex-col gap-[8px]">
    <h2 className="text-[18px] font-semibold text-[#0A0A0A]">Automated (BCC) Email</h2>
    <p className="text-[13px] leading-[1.5] text-[#0A0A0A]">To record emails sent from outside the CMP dashboard, add this email address as a BCC to automatically record a copy of the email in the member&apos;s Communication Log.</p>
    {loading ? <p role="status" className="text-[14px]">Loading email address...</p> : error ? <div role="alert" className="text-[14px] text-red-600">The email address could not be loaded. <button type="button" className="underline" onClick={retry}>Try again</button></div> : address ? <div className="flex flex-wrap items-center gap-2.5"><span className="min-w-0 break-all rounded-lg border border-[#ECECEC] bg-[#F7F7F8] px-3.5 py-[11px] text-[14px]">{address}</span><button type="button" onClick={copy} className="flex items-center gap-2 rounded-lg border border-[#9E9E9E] bg-white px-4 py-[11px] text-[13px] font-semibold hover:bg-[#F7F7F8]"><ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />{copied ? "Copied!" : "Copy"}</button></div> : <p role="status" className="text-[14px]">{`Your Automated (BCC) Email address is not set up yet. To set it up, email ${STAFF_SUPPORT_EMAIL}.`}</p>}
    {address && !status?.captureEnabled && <p role="status" className="text-[14px]">{`This address is not turned on yet. Do not use it until it is turned on. To turn it on, email ${STAFF_SUPPORT_EMAIL}.`}</p>}
    {copyError && <p role="alert" className="text-[14px] text-red-600">The address could not be copied. Select the address and copy it.</p>}
  </section>;
};
export default EmailCaptureAddressSection;
